/**
 * Two-state theme toggle: `light` (forces .light-theme on <html>) /
 * `dark` (forces .dark-theme). Persisted to localStorage; a fresh visit
 * initialises from the system's prefers-color-scheme but stays fixed
 * after the first click.
 */
export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "theme-preference";

export function readStoredTheme(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* private mode — ignore */
  }
  return detectSystem();
}

export function applyTheme(mode: ThemeMode): void {
  const html = document.documentElement;
  html.classList.remove("light-theme", "dark-theme");
  if (mode === "light") html.classList.add("light-theme");
  if (mode === "dark") html.classList.add("dark-theme");
}

export function bindThemeToggle(button: HTMLButtonElement): void {
  let current = readStoredTheme();
  applyTheme(current);
  syncButton(button, current);

  button.addEventListener("click", () => {
    current = next(current);
    persist(current);
    applyTheme(current);
    syncButton(button, current);
  });
}

function next(mode: ThemeMode): ThemeMode {
  return mode === "light" ? "dark" : "light";
}

function persist(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* private mode — ignore */
  }
}

function syncButton(button: HTMLButtonElement, mode: ThemeMode): void {
  button.dataset["state"] = mode;
  button.setAttribute("aria-label", `テーマ切替（現在: ${labelJa(mode)}）`);
  button.setAttribute("title", `Theme: ${mode}`);
}

function labelJa(mode: ThemeMode): string {
  return mode === "light" ? "ライト" : "ダーク";
}

function detectSystem(): ThemeMode {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
